import { Size, rangeEndMin, rangeIsOrdered, t, zoneNoteNeeded } from '@bms2/shared';
import { useViewerZone } from '../../shell/ViewerZone';
import { FieldShell } from './FieldShell';
import styles from './field.module.css';

/**
 * Date and Date-Time picker (piece 3): the native date and datetime-local
 * inputs with the plan's wrapper, which adds the label, the required marker,
 * the error state and the zone note, and draws no calendar of its own. The
 * picker states which zone it shows wherever the viewer's differs from the
 * record's. A range is two natives side by side, the end bounded by the start.
 */
export interface DatePickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  mode?: 'date' | 'datetime';
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  min?: string;
  max?: string;
  /** The record's own zone; a note names the viewer's zone when the two differ. */
  recordTimeZone?: string | null;
  name?: string;
  /**
   * Which of the three widths the box takes; a day is Small and a day with a clock
   * Medium (decision 0583). 🔨 A clock never fits Small, so a datetime asked for
   * at Small draws Medium: the control holds its own floor rather than every caller.
   * 🔒 A plain date holds a floor of its own, beneath Small (T69; decision 0652):
   * it never draws narrower than ten characters and the calendar button need,
   * whichever size names it.
   */
  size?: Size;
  /** The label leaves the screen and stays in the accessibility tree: a condition row's value, whose row names it (decision 2175). */
  labelHidden?: boolean;
}

export const datePickerDocumentedStates = ['date', 'dateTime', 'zoneNote', 'error', 'range'] as const;

/**
 * 🔨 The viewer's zone is the frame's, which is the person's own setting —
 * never the browser's guess (decision 0261). 💰 The picker compared the
 * record's zone against whatever clock the machine happened to be on, so the
 * note appeared and vanished with travel (found while building, 2026-09-09).
 */
function zoneNote(viewer: string, recordTimeZone: string | null | undefined): string | null {
  if (!zoneNoteNeeded(viewer, recordTimeZone)) return null;
  return `${t('date.zoneNote')} (${viewer}). ${t('date.recordZone')} ${recordTimeZone}.`;
}

export function DatePicker({ label, value, onChange, mode = 'date', required, error, disabled, min, max, recordTimeZone, name, size = Size.Medium, labelHidden = false }: DatePickerProps) {
  const viewer = useViewerZone();
  const note = mode === 'datetime' || recordTimeZone ? zoneNote(viewer, recordTimeZone) : null;
  const width = mode === 'datetime' && size === Size.Small ? Size.Medium : size;
  return (
    <FieldShell label={label} labelHidden={labelHidden} required={required ?? false} error={error ?? null} disabled={disabled ?? false} hint={note} size={width} dateFloor={mode === 'datetime' ? 'datetime' : 'date'}>
      {({ inputId, messageId }) => (
        <input
          id={inputId}
          className={`${styles.input} ${styles.withIcon}`}
          type={mode === 'datetime' ? 'datetime-local' : 'date'}
          name={name}
          value={value}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          data-zone-note={note ? 'true' : 'false'}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? messageId : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </FieldShell>
  );
}

export interface DateRangeProps {
  label: string;
  start: string;
  end: string;
  onChange: (range: { start: string; end: string }) => void;
  mode?: 'date' | 'datetime';
  disabled?: boolean;
}

/**
 * Two bounded natives, and the control's own comparison beside them. 🔨 `min` is
 * the affordance: a desktop picker greys out the earlier dates so nobody can
 * pick one, while an iPhone's picker lets the value through and only marks the
 * field invalid. The error below is what refuses it on every browser.
 */
export function DateRange({ label, start, end, onChange, mode = 'date', disabled }: DateRangeProps) {
  const endMin = rangeEndMin(start);
  const outOfOrder = !rangeIsOrdered(start, end);
  const size = mode === 'datetime' ? Size.Medium : Size.Small;
  return (
    <fieldset className={styles.pair} data-date-range="true" data-range-error={outOfOrder} aria-label={label} style={{ border: 'none', margin: 0, padding: 0 }}>
      <DatePicker size={size} label={t('date.rangeStart')} value={start} onChange={(v) => onChange({ start: v, end })} mode={mode} disabled={disabled ?? false} />
      <DatePicker size={size}
        label={t('date.rangeEnd')}
        value={end}
        onChange={(v) => onChange({ start, end: v })}
        mode={mode}
        disabled={disabled ?? false}
        error={outOfOrder ? t('date.rangeOutOfOrder') : null}
        {...(endMin === undefined ? {} : { min: endMin })}
      />
    </fieldset>
  );
}
